import { useState, useCallback } from "react";
import api from "../../../services/api";

/**
 * 🎭 Hook useMessageReactions
 * 
 * Centraliza toda a lógica relacionada às reações de mensagens:
 * - Contagem e processamento de reações
 * - Manipulação de adição/remoção de reações
 * - Gerenciamento do modal de detalhes
 * - Estados de controle e atualização
 * 
 * @param {boolean} isGroup - Se é uma conversa em grupo ou individual
 * @param {function} dispatch - Função dispatch para atualizar o estado das mensagens
 * @returns {object} Objeto com estados e funções relacionadas às reações
 */
export const useMessageReactions = (isGroup, dispatch) => {
  // 🎯 Estados centralizados das reações
  const [reactionModalOpen, setReactionModalOpen] = useState(false);
  const [selectedMessageReactions, setSelectedMessageReactions] = useState([]);
  const [reactionUpdateTrigger, setReactionUpdateTrigger] = useState(0);

  // 🚀 Função para contar reações memoizada
  const contarOcorrencias = useCallback((arrayReaction) => {
    const contagem = {};

    if (!arrayReaction || (arrayReaction.length <= 0)) {
      return []
    }

    // Filtrar apenas reações válidas (emojis) e evitar duplicatas por usuário
    const uniqueReactions = [];
    const seenUserReactions = new Map(); // Map para rastrear a reação mais recente de cada usuário
    
    arrayReaction
      .filter(obj => obj.body && obj.body.trim() && obj.body !== "reaction" && obj.body !== "") // Filtrar reações inválidas
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Ordenar por mais recente primeiro
      .forEach(obj => {
        const userKey = obj.fromMe ? 'me' : obj.contactId;
        
        // Em conversas individuais, cada usuário pode ter apenas uma reação
        // Em conversas em grupo, cada usuário pode ter uma reação por emoji
        const reactionKey = isGroup ? `${userKey}_${obj.body}` : userKey;
        
        if (!seenUserReactions.has(reactionKey)) {
          seenUserReactions.set(reactionKey, true);
          uniqueReactions.push(obj);
        }
      });

    // Contar ocorrências dos emojis únicos
    uniqueReactions.forEach(obj => {
      contagem[obj.body] = (contagem[obj.body] || 0) + 1;
    });

    // Transformar o objeto de contagem em um array
    return Object.entries(contagem).map(([body, quantidade]) => ({ body, quantidade }));
  }, [isGroup]);

  // 🎯 Handler principal para reações (adicionar/remover)
  const handleFloatingReact = useCallback(async (message, emoji) => {
    try {
      // Verificar se o usuário já reagiu com este emoji
      const existingReaction = message.reactionsMsg?.find(
        reaction => reaction.body === emoji && reaction.fromMe
      );

      // Em conversas individuais, verificar se o usuário já reagiu com qualquer emoji
      const existingUserReaction = !isGroup && message.reactionsMsg?.find(
        reaction => reaction.fromMe
      );

      if (existingReaction) {
        // Se já reagiu com este emoji, remove a reação enviando uma reação vazia
        await api.post(`/messages/${message.id}/reactions`, {
          type: "", // Reação vazia remove a reação anterior
        });
        console.log("Reação removida:", emoji);
      } else {
        // Se não reagiu com este emoji, adiciona a reação
        await api.post(`/messages/${message.id}/reactions`, {
          type: emoji,
        });
        console.log("Reação adicionada:", emoji);
      }

      // Forçar atualização da mensagem localmente para feedback imediato
      const updatedMessage = { ...message };
      
      if (existingReaction) {
        // Remove a reação específica localmente
        updatedMessage.reactionsMsg = message.reactionsMsg.filter(
          reaction => !(reaction.body === emoji && reaction.fromMe)
        );
      } else {
        // Adiciona a nova reação localmente
        if (!updatedMessage.reactionsMsg) {
          updatedMessage.reactionsMsg = [];
        }

        // Em conversas individuais, remover reação anterior do usuário antes de adicionar a nova
        if (!isGroup && existingUserReaction) {
          updatedMessage.reactionsMsg = message.reactionsMsg.filter(
            reaction => !reaction.fromMe
          );
        }

        updatedMessage.reactionsMsg.push({
          id: Date.now(), // ID temporário
          fromMe: true,
          body: emoji,
          contactId: null,
          createdAt: new Date().toISOString()
        });
      }
      
      dispatch({ type: "UPDATE_MESSAGE", payload: updatedMessage });

    } catch (err) {
      console.error("Erro ao gerenciar reação:", err);
      // Em caso de erro, reverter a atualização otimista
      dispatch({ type: "UPDATE_MESSAGE", payload: message });
    }
  }, [dispatch, isGroup]);

  // 🎯 Função para abrir modal de detalhes das reações
  const handleOpenReactionDetails = useCallback((message) => {
    if (message.reactionsMsg && message.reactionsMsg.length > 0) {
      setSelectedMessageReactions(message.reactionsMsg);
      setReactionModalOpen(true);
    }
  }, []);

  // 🎯 Função para fechar modal de detalhes das reações
  const handleCloseReactionDetails = useCallback(() => {
    setReactionModalOpen(false);
    setSelectedMessageReactions([]);
  }, []);

  // 🎯 Função para forçar atualização (usada pelos WebSockets)
  const forceReactionUpdate = useCallback(() => {
    setReactionUpdateTrigger(prev => prev + 1);
  }, []);

  // 🎯 Função para verificar se mensagens consecutivas têm reações
  const hasConsecutiveReactions = useCallback((messagesList, currentIndex, fromMe) => {
    if (currentIndex === 0) return false;

    const previousMessage = messagesList[currentIndex - 1];
    const currentMessage = messagesList[currentIndex];

    // Verifica se são do mesmo remetente e ambas têm reações
    return (
      previousMessage?.fromMe === fromMe &&
      previousMessage?.reactionsMsg?.length > 0 &&
      currentMessage?.reactionsMsg?.length > 0
    );
  }, []);

  return {
    // 📊 Estados
    reactionModalOpen,
    selectedMessageReactions,
    reactionUpdateTrigger,
    
    // 🔧 Funções principais
    contarOcorrencias,
    handleFloatingReact,
    handleOpenReactionDetails,
    handleCloseReactionDetails,
    forceReactionUpdate,
    hasConsecutiveReactions,
  };
};

export default useMessageReactions;
